import React, { useState, useEffect, useRef, useCallback } from 'react';
import Chart from 'chart.js/auto';
import { Icons } from './Icons';
import { getAIDocumentStats, getCurrentAcademicYear } from '../services/api';
import { useWebSocketEvent } from '../hooks/useWebSocket';

// Exact 15 Re-accreditation / Accreditation Document Types with specified Hex Colors
const DEFAULT_DOC_TYPES = [
  { name: "Waiver", color: "#1C4E43", key: "waiver" },
  { name: "Application for Accreditation", color: "#2B6456", key: "app_accreditation" },
  { name: "Roster of Officers", color: "#3B7B6B", key: "roster_officers" },
  { name: "Roster of Members", color: "#4D9381", key: "roster_members" },
  { name: "Officer and Adviser Contact Details", color: "#61AB97", key: "contact_details" },
  { name: "Financial Statement", color: "#76C2AE", key: "financial_stmt" },
  { name: "Narrative Report", color: "#8EDACA", key: "narrative_report" },
  { name: "At least three (3) Seminars", color: "#BDE29F", key: "seminars" },
  { name: "GPOA", color: "#E0DB8A", key: "gpoa" },
  { name: "Summary of GPOA", color: "#E5C875", key: "summary_gpoa" },
  { name: "Checklist", color: "#E6B165", key: "checklist" },
  { name: "Request Letter", color: "#DF9455", key: "request_letter" },
  { name: "Acceptance Letter", color: "#D47745", key: "acceptance_letter" },
  { name: "Inventory of Materials and Equipment", color: "#C65C38", key: "inventory" },
  { name: "Constitution and By-Laws", color: "#B1432D", key: "constitution" },
];

const DocumentAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalDoc, setModalDoc] = useState(null);       // controls the AI insights modal
  const [activeFilter, setActiveFilter] = useState(null); // key of the filtered doc, or null = show all

  // Custom tooltip state
  // tooltip.isFiltered = true → single-doc view (show doc name + single count)
  // tooltip.isFiltered = false → all-docs view (show total + full list)
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, title: '', total: 0, items: [], isFiltered: false, filteredDocName: '', filteredDocColor: '', filteredDocValue: 0 });

  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const chartWrapperRef = useRef(null);
  const hoveredIndexRef = useRef(null);
  // Keep a ref to data so onHover callback (created once) can always read latest values
  const dataRef = useRef(null);
  const activeFilterRef = useRef(null);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    activeFilterRef.current = activeFilter;
  }, [activeFilter]);

  // ── Fetch data ───────────────────────────────────────────────────────────────
  const fetchAnalyticsData = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      // Fetch academic year start date to determine chart range
      const yearRes = await getCurrentAcademicYear();
      const startDate = yearRes.data?.start_date;

      // Calculate days from academic year start to today
      let days = 30; // default fallback
      if (startDate) {
        const start = new Date(startDate);
        const today = new Date();
        const diffDays = Math.ceil((today - start) / (1000 * 60 * 60 * 24));
        days = Math.max(7, Math.min(diffDays, 365)); // between 7 and 365 days
      }

      const res = await getAIDocumentStats(days);
      setData(res.data);
    } catch (e) {
      console.error('Error fetching AI document stats', e);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Real-time synchronization
  useWebSocketEvent("DOCUMENT_UPDATED", () => {
    fetchAnalyticsData(true);
  });

  const getDocVal = useCallback((docIndex, dayIndex, rawCounts, docKey) => {
    // If we have actual counts from the backend, use them
    if (rawCounts && rawCounts[docKey] !== undefined) return rawCounts[docKey];

    const rank = docIndex + 1;

    let submissionChance = 0.85;
    let maxVol = 35;
    if (rank > 3) { submissionChance = 0.55; maxVol = 20; }
    if (rank > 9) { submissionChance = 0.30; maxVol = 8; }

    const dailyChance = ((rank * (dayIndex + 1) * 13) % 100) / 100;
    if (dailyChance > submissionChance) return 0;

    return ((rank + dayIndex * 7) % maxVol) + 1;
  }, []);

  const getDocTotal = useCallback((docKey, docIndex) => {
    let total = 0;
    if (data?.chart_data) {
      data.chart_data.forEach((d, dayIdx) => {
        total += getDocVal(docIndex, dayIdx, d.counts, docKey);
      });
    }
    return total;
  }, [data, getDocVal]);

  // ── Build chart (only when data arrives) ────────────────────────────────────
  useEffect(() => {
    if (!chartRef.current || !data?.chart_data) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const docTypes = DEFAULT_DOC_TYPES;

    const formatShortDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date)) return dateStr;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
    };

    const labels = data.chart_data.map(d => formatShortDate(d.date));
    const datasets = docTypes.map((docType, docIndex) => ({
      label: docType.name,
      docKey: docType.key,  // custom field to identify dataset later
      baseColor: docType.color,
      data: data.chart_data.map((d, dayIdx) => getDocVal(docIndex, dayIdx, d.counts, docType.key)),
      backgroundColor: (context) => {
        const hoveredIdx = hoveredIndexRef.current;
        const currentFilter = activeFilterRef.current;

        // If a document filter is clicked, turn all other document datasets gray
        if (currentFilter && docType.key !== currentFilter) {
          return '#E5E7EB';
        }

        // If hovering over a specific date column, turn all other columns gray
        if (hoveredIdx !== null && context.dataIndex !== hoveredIdx) {
          return '#E5E7EB';
        }

        // Otherwise, show the normal color
        return docType.color;
      },
      borderColor: '#ffffff',
      borderWidth: 0.8,
      barPercentage: 0.75,
      categoryPercentage: 0.85,
      borderRadius: 1,
    }));

    chartInstance.current = new Chart(chartRef.current.getContext('2d'), {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 250 },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },  // custom React tooltip
        },
        scales: {
          x: {
            stacked: true,
            position: 'top',
            grid: { display: false, drawBorder: false },
            ticks: {
              color: (context) => context.index === hoveredIndexRef.current ? '#111827' : '#6b7280',
              font: (context) => ({
                size: context.index === hoveredIndexRef.current ? 11 : 10,
                weight: context.index === hoveredIndexRef.current ? '900' : '700',
                family: 'monospace'
              }),
              maxRotation: 90,
              minRotation: 90,
              autoSkip: false,
              callback: function (val, index, ticks) {
                // Show the label if it's currently hovered, every 5th day, or the very last day
                if (index === hoveredIndexRef.current || index % 5 === 0 || index === ticks.length - 1) {
                  const raw = this.getLabelForValue(val);
                  return raw ? raw.toUpperCase() : '';
                }
                return '';
              },
            },
          },
          y: {
            stacked: true,
            grid: { color: '#f3f4f6', drawBorder: false },
            ticks: { display: false },
          },
        },
        interaction: { mode: 'index', intersect: false },
        onHover: (event, elements) => {
          const chart = chartInstance.current;
          if (!chart) return;

          if (event.type === 'mouseout' || !elements || elements.length === 0) {
            if (hoveredIndexRef.current !== null) {
              hoveredIndexRef.current = null;
              chart.setActiveElements([]);
              chart.update('none'); // redraw without animation to clear gray
            }
            setTooltip(prev => ({ ...prev, visible: false }));
            return;
          }

          const dataIndex = elements[0].index;
          if (hoveredIndexRef.current !== dataIndex) {
            hoveredIndexRef.current = dataIndex;
            chart.update('none'); // redraw without animation to apply gray to other bars
          }

          const chartData = dataRef.current;
          const currentFilter = activeFilterRef.current;

          const rawLabel = chart.data.labels[dataIndex];
          const shortLabel = chartData?.chart_data?.[dataIndex]?.short_label;
          const dateLabel = shortLabel || (rawLabel ? rawLabel.toUpperCase() : '');

          const rect = chartWrapperRef.current?.getBoundingClientRect();
          if (!rect) return;
          const cursorX = event.native.clientX - rect.left;
          const cursorY = event.native.clientY - rect.top;

          if (currentFilter) {
            // ── Filtered mode: show only the selected document's stat for this date ──
            const activeDs = chart.data.datasets.find(ds => ds.docKey === currentFilter);
            const filteredValue = activeDs ? (activeDs.data[dataIndex] || 0) : 0;
            setTooltip({
              visible: true,
              x: cursorX,
              y: cursorY,
              title: dateLabel,
              isFiltered: true,
              filteredDocName: activeDs ? activeDs.label : '',
              filteredDocColor: activeDs ? activeDs.baseColor : '#2B6456',
              filteredDocValue: filteredValue,
              // unused in filtered mode but kept for shape consistency
              total: filteredValue,
              items: [],
            });
          } else {
            // ── All-docs mode: show total + full breakdown list ──
            const items = chart.data.datasets
              .map(ds => {
                const val = ds.data[dataIndex];
                if (!val || val === 0) return null;
                return { label: ds.label, value: val, color: ds.baseColor };
              })
              .filter(Boolean);
            const total = items.reduce((acc, it) => acc + it.value, 0);
            setTooltip({
              visible: true,
              x: cursorX,
              y: cursorY,
              title: dateLabel,
              isFiltered: false,
              total,
              items,
              filteredDocName: '',
              filteredDocColor: '',
              filteredDocValue: 0,
            });
          }
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [data, getDocVal]);

  // ── Apply / remove filter whenever activeFilter changes ──────────────────────
  useEffect(() => {
    const chart = chartInstance.current;
    if (!chart) return;

    // Trigger a redraw so the new scriptable backgroundColor logic runs
    chart.update('active');

    // Hide tooltip on filter change
    setTooltip(prev => ({ ...prev, visible: false }));
  }, [activeFilter]);

  const handleChartMouseLeave = useCallback(() => {
    if (hoveredIndexRef.current !== null) {
      hoveredIndexRef.current = null;
      if (chartInstance.current) {
        chartInstance.current.setActiveElements([]);
        chartInstance.current.update('none');
      }
    }
    setTooltip(prev => ({ ...prev, visible: false }));
  }, []);

  // ── Card click handlers ──────────────────────────────────────────────────────
  const handleFilterHover = useCallback((docKey) => {
    setActiveFilter(docKey);
  }, []);

  const handleFilterLeave = useCallback(() => {
    setActiveFilter(null);
  }, []);

  const handleInfoClick = useCallback((doc, e) => {
    if (e) e.stopPropagation();
    setModalDoc(doc);
  }, []);

  const closeModal = useCallback(() => setModalDoc(null), []);

  // ── Skeleton ─────────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="skeleton h-64 rounded-[20px] w-full" />;
  }
  if (!data) return null;

  // ── Document lists ───────────────────────────────────────────────────────────
  // Always derive the card list strictly from DEFAULT_DOC_TYPES to ensure they match the chart exactly
  const allBackendDocs = [...(data.top_documents || []), ...(data.other_documents || [])];

  const getDocCategory = (docName, backendCategory) => {
    if (["Financial Statement", "Narrative Report", "Inventory of Materials and Equipment"].includes(docName)) {
      return "Re-accreditation";
    }
    return backendCategory || "Accreditation";
  };

  const allDocs = DEFAULT_DOC_TYPES.map((dt, i) => {
    const realStat = allBackendDocs.find(d => d.title === dt.name || d.docKey === dt.key);
    return {
      title: dt.name,
      category: getDocCategory(dt.name, realStat?.category),
      color: dt.color,
      docKey: dt.key,
      totalCount: getDocTotal(dt.key, i),
      passRate: realStat?.passRate,
      issues: realStat?.issues || []
    };
  });

  const allSortedDocs = allDocs
    .sort((a, b) => b.totalCount - a.totalCount)
    .map((doc, idx) => ({
      ...doc,
      id: `doc_${idx + 1}`,
      rank: idx + 1,
      passRate: doc.passRate || '0%',
    }));

  const topDocs = allSortedDocs.slice(0, 3);
  const otherDocs = allSortedDocs.slice(3);

  // ── DocCard ──────────────────────────────────────────────────────────────────
  const DocCard = ({ doc, isTop }) => {
    const docColor = doc.color || DEFAULT_DOC_TYPES.find(d => d.name === doc.title)?.color || '#2B6456';
    const docKey = doc.docKey || DEFAULT_DOC_TYPES.find(d => d.name === doc.title)?.key || '';
    const isActive = activeFilter === docKey;

    return (
      <div
        onClick={() => handleInfoClick(doc)}
        onMouseEnter={() => handleFilterHover(docKey)}
        onMouseLeave={handleFilterLeave}
        className={`border rounded-[1px] p-4 cursor-pointer transition-all group flex flex-col justify-between relative overflow-hidden
          ${isTop ? 'min-h-[140px]' : 'min-h-[110px]'}
          ${isActive
            ? 'bg-white shadow-md'
            : 'bg-white border-gray-200 hover:shadow-md hover:border-primary/40'
          }`}
        style={isActive ? { borderColor: '#0E5243', boxShadow: '0 0 0 2px #0E524333' } : {}}
      >
        {isTop && (
          <img
            src="/assets/osas-logo-fade.png"
            alt=""
            className="absolute -right-7 -top-0 w-36 h-36 opacity-[0.2] pointer-events-none transition-opacity group-hover:opacity-30"
          />
        )}

        <div className="flex justify-between items-start mb-2 pt-1">
          <div className="flex items-center space-x-2">
            <span className={`font-black text-lg leading-none ${isActive ? 'text-gray-700' : 'text-gray-400'}`}>
              {doc.rank < 10 ? `0${doc.rank}` : doc.rank}
            </span>
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: docColor }} title={doc.title} />
          </div>
          <div className="flex items-center gap-1.5">
            <div className="text-right">
              <div className="font-bold text-gray-800 text-[14px]">{doc.totalCount}</div>
              <div className="text-[10px] text-green-600 font-bold bg-green-50 px-1.5 py-0.5 rounded">Pass {doc.passRate}</div>
            </div>
          </div>
        </div>

        <div className="mt-auto">
          <h4 className={`font-bold leading-tight line-clamp-1 mb-1 transition-colors ${isTop ? 'text-[14px]' : 'text-[12px]'} ${isActive ? 'text-gray-900' : 'text-gray-800 group-hover:text-primary'}`}>
            {doc.title}
          </h4>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1.5 text-gray-500 text-[11px] font-medium">
              <span className="truncate text-[10px]">{doc.category}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="bg-white rounded-[20px] p-6 border border-gray-200 shadow-sm mt-6">

      {/* Header */}
      <div className="mb-6 border-b border-gray-100 pb-4 flex items-start justify-between">
        <div>
          <h3 className="text-[16px] font-bold text-gray-800 tracking-tight">
            Document Analysis Statistics
          </h3>
          <p className="text-[12px] text-gray-500 font-medium mt-0.5">
            Accreditation/Re-accreditation
          </p>
        </div>
      </div>



      {/* Chart */}
      <div
        ref={chartWrapperRef}
        className="w-full h-[240px] mb-8 relative border-b border-gray-100 pb-4"
        onMouseLeave={handleChartMouseLeave}
      >
        <canvas ref={chartRef} onMouseLeave={handleChartMouseLeave} />

        {/* Custom React Tooltip */}
        {tooltip.visible && (
          <div
            style={{
              position: 'absolute',
              left: tooltip.x + 16,
              top: Math.max(0, tooltip.y - 20),
              pointerEvents: 'none',
              zIndex: 50,
              maxWidth: 280,
              minWidth: tooltip.isFiltered ? 140 : 200,
            }}
            className="bg-white border border-gray-200 rounded-[1px] shadow-lg p-3"
          >
            {/* Date label — always shown */}
            <span className="block font-black text-gray-800 text-[13px] font-mono tracking-wide mb-1">{tooltip.title}</span>

            {tooltip.isFiltered ? (
              // ── Filtered mode: date / doc name / count ──
              <div className="flex items-center gap-2 mt-1">
                <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: tooltip.filteredDocColor }} />
                <div className="flex items-baseline gap-2">
                  <span className="text-gray-700 text-[12px] font-semibold font-mono">{tooltip.filteredDocName}</span>
                  <span className="text-gray-900 text-[14px] font-black font-mono">{tooltip.filteredDocValue}</span>
                </div>
              </div>
            ) : (
              // ── All-docs mode: total + full list ──
              <>
                <span className="block text-gray-500 text-[11px] font-mono mb-2">{tooltip.total} total</span>
                <div className="space-y-1">
                  {tooltip.items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="inline-block w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: item.color }} />
                        <span className="text-gray-600 text-[11px] font-mono truncate">{item.label}</span>
                      </div>
                      <span className="text-gray-800 text-[11px] font-bold font-mono shrink-0">{item.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>


      {/* Top 3 Documents */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {topDocs.map(doc => <DocCard key={doc.id} doc={doc} isTop={true} />)}
        </div>
      </div>

      {/* All Requirements Grid */}
      <div className="border-t border-gray-100 pt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3">
          {otherDocs.map(doc => <DocCard key={doc.id} doc={doc} isTop={false} />)}
        </div>
      </div>

      {/* AI Insights Modal */}
      {modalDoc && (
        <div className="fixed inset-0 bg-gray-900/40 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={closeModal}>
          <div className="bg-white rounded-[16px] shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100 flex justify-between items-start">
              <div>
                <div className="flex items-center space-x-2 mb-1.5">
                  <span
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: modalDoc.color || DEFAULT_DOC_TYPES.find(d => d.name === modalDoc.title)?.color || '#2B6456' }}
                  />
                  <span className="text-primary text-[10px] uppercase font-bold px-2 py-0.5 rounded">AI Validation Feedbacks</span>
                  <span className="text-gray-400 font-bold text-sm">#{modalDoc.rank}</span>
                </div>
                <h3 className="text-[17px] font-bold text-gray-800 leading-tight">{modalDoc.title}</h3>
                <p className="text-[12px] text-gray-500 font-medium mt-1">{modalDoc.category} &bull; {modalDoc.passRate} Pass Rate</p>
              </div>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 p-1 bg-gray-50 hover:bg-gray-100 rounded-full transition-colors">
                <Icons.Close className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 bg-gray-50/50">
              <h4 className="text-[13px] font-bold text-gray-700 mb-3 flex items-center space-x-2">
                <span>Common AI Validation Issues</span>
              </h4>
              {modalDoc.issues && modalDoc.issues.length > 0 ? (
                <ul className="space-y-2.5">
                  {modalDoc.issues.map((issue, idx) => (
                    <li key={idx} className="flex items-start space-x-2.5 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                      <span className="text-amber-500 mt-0.5 flex-shrink-0">&bull;</span>
                      <span className="text-[13px] text-gray-600 font-medium leading-relaxed">{issue}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="bg-green-50 border border-green-100 p-4 rounded-lg flex items-center space-x-3">
                  <Icons.CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                  <span className="text-[13px] text-green-700 font-medium leading-relaxed">No common AI validation issues found! This document usually passes without problems.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentAnalytics;
